import React from 'react';
import {
  Create,
  SimpleForm,
  TextInput,
  NumberInput,
  SelectInput,
  ReferenceInput,
  DateInput,
  ArrayInput,
  SimpleFormIterator,
  required,
} from 'react-admin';
import { Box, Typography } from '@mui/material';

const transform = (data: any) => {
  // Ensure all numeric fields have proper values
  const lineItems = data.lineItems || [];
  let subtotal = 0;

  // Calculate subtotal from line items
  const processedLineItems = lineItems.map((item: any) => {
    const quantity = parseFloat(item?.quantity?.toString() || '0') || 0;
    const unitPrice = parseFloat(item?.unitPrice?.toString() || '0') || 0;
    const amount = Number((quantity * unitPrice).toFixed(2));
    subtotal += amount;

    return {
      ...item,
      quantity,
      unitPrice,
      amount,
    };
  });

  // Calculate tax and totals
  const taxRate = parseFloat(data.taxRate?.toString() || '0') || 0;
  const taxAmount = Number((subtotal * (taxRate / 100)).toFixed(2));
  const totalAmount = Number((subtotal + taxAmount).toFixed(2));
  const paidAmount = parseFloat(data.paidAmount?.toString() || '0') || 0;
  const balanceAmount = Number((totalAmount - paidAmount).toFixed(2));

  return {
    ...data,
    lineItems: processedLineItems,
    subtotal: Number(subtotal.toFixed(2)),
    taxRate,
    taxAmount,
    totalAmount,
    paidAmount,
    balanceAmount,
    // Ensure payments array is properly formatted
    payments: (data.payments || []).map((payment: any) => ({
      ...payment,
      amount: parseFloat(payment?.amount?.toString() || '0') || 0,
    })),
  };
};

export const InvoiceCreate = () => (
  <Create transform={transform}>
    <SimpleForm>
      <Typography variant="h6" gutterBottom>
        Basic Information
      </Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2, mb: 2 }}>
        <TextInput source="invoiceNumber" label="Invoice Number" validate={required()} />
        <TextInput source="title" validate={required()} />
        <ReferenceInput source="customerId" reference="customers">
          <SelectInput optionText="displayName" validate={required()} />
        </ReferenceInput>
        <SelectInput
          source="status"
          label="Status"
          choices={[
            { id: 'draft', name: 'Draft' },
            { id: 'sent', name: 'Sent' },
            { id: 'paid', name: 'Paid' },
            { id: 'overdue', name: 'Overdue' },
            { id: 'cancelled', name: 'Cancelled' },
          ]}
          defaultValue="draft"
          validate={required()}
        />
        <DateInput 
          source="dueDate" 
          label="Due Date" 
          validate={required()} 
          parse={(value) => value ? new Date(value + 'T00:00:00').toISOString() : null}
          format={(value) => value ? value.split('T')[0] : ''}
        />
      </Box>

      <Typography variant="h6" gutterBottom>
        Line Items
      </Typography>
      <ArrayInput source="lineItems">
        <SimpleFormIterator inline>
          <TextInput source="description" label="Description" validate={required()} />
          <NumberInput
            source="quantity"
            label="Quantity"
            validate={required()}
            defaultValue={1}
            min={0}
          />
          <NumberInput
            source="unitPrice"
            label="Unit Price"
            validate={required()}
            defaultValue={0}
            min={0}
            step={0.01}
          />
          <NumberInput
            source="amount"
            label="Amount"
            disabled
            helperText="Calculated automatically"
          />
        </SimpleFormIterator>
      </ArrayInput>

      <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
        Financial Details
      </Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2, mb: 2 }}>
        <NumberInput
          source="subtotal"
          label="Subtotal"
          disabled
          defaultValue={0}
          helperText="Calculated from line items"
        />
        <NumberInput
          source="taxRate"
          label="Tax Rate (%)"
          defaultValue={0}
          min={0}
          max={100}
          step={0.01}
        />
        <NumberInput
          source="taxAmount"
          label="Tax Amount"
          disabled
          defaultValue={0}
          helperText="Calculated automatically"
        />
        <NumberInput
          source="totalAmount"
          label="Total Amount"
          disabled
          defaultValue={0}
          helperText="Subtotal + Tax"
        />
        <NumberInput source="paidAmount" label="Paid Amount" defaultValue={0} min={0} step={0.01} />
        <NumberInput
          source="balanceAmount"
          label="Balance Amount"
          disabled
          defaultValue={0}
          helperText="Total - Paid"
        />
      </Box>

      <Typography variant="h6" gutterBottom>
        Payment History (Optional)
      </Typography>
      <ArrayInput source="payments">
        <SimpleFormIterator inline>
          <DateInput 
            source="paymentDate" 
            label="Payment Date" 
            parse={(value) => value ? new Date(value + 'T00:00:00').toISOString() : null}
            format={(value) => value ? value.split('T')[0] : ''}
          />
          <NumberInput source="amount" label="Amount" min={0} step={0.01} defaultValue={0} />
          <SelectInput
            source="method"
            label="Payment Method"
            choices={[
              { id: 'cash', name: 'Cash' },
              { id: 'check', name: 'Check' },
              { id: 'credit_card', name: 'Credit Card' },
              { id: 'bank_transfer', name: 'Bank Transfer' },
              { id: 'paypal', name: 'PayPal' },
              { id: 'other', name: 'Other' },
            ]}
            defaultValue="cash"
          />
          <TextInput source="notes" label="Notes" />
        </SimpleFormIterator>
      </ArrayInput>

      <Typography variant="h6" gutterBottom>
        Additional Information
      </Typography>
      <TextInput source="notes" label="Internal Notes" multiline rows={3} fullWidth />
      <TextInput source="terms" label="Terms & Conditions" multiline rows={2} fullWidth />
    </SimpleForm>
  </Create>
);
